import Swup from "swup";
import SwupScrollPlugin from "@swup/scroll-plugin";
import SwupBodyClassPlugin from "@swup/body-class-plugin";

import { initMobileNav, closeMobileNav } from "./components/mobile-nav";
import { initBlurUp } from "./components/blur-up";
import { initFilters } from "./components/filters";
import {
  initFooterIllustration,
  resetFooterIllustration,
} from "./components/footer-illustration";
import { initFooterReveal } from "./components/footer-reveal";
import { initLogoShuffle } from "./components/logo-shuffle";
import { initMarquee, destroyMarquee } from "./components/marquee";
import { initHeroLogo } from "./components/hero-logo";
import { initHomeBackground } from "./components/home-background";
import { initServicesHighlight } from "./components/services-highlight";
import { initMembersOnlyGate } from "./components/members-only-gate";
import { initMembersHeroCount } from "./components/members-hero-count";
import { initCtaCardGlow } from "./components/cta-card-glow";
import { initOverlayClose } from "./components/overlay-close";
import { initInlineOverlay } from "./components/inline-overlay";
import { initNewsletterCopy } from "./components/newsletter-copy";

const OVERLAY_SCROLL_KEY = "overlayScrollPositions";

const getOverlayScrollMap = () => {
  try {
    const raw = sessionStorage.getItem(OVERLAY_SCROLL_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const setOverlayScrollMap = (map) => {
  try {
    sessionStorage.setItem(OVERLAY_SCROLL_KEY, JSON.stringify(map));
  } catch {
    // Ignore storage errors
  }
};

const rememberOverlayTriggerScroll = () => {
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const overlayLink = target.closest("a[data-overlay]");
    if (!(overlayLink instanceof HTMLAnchorElement)) return;

    const key = window.location.pathname.replace(/\/$/, "") || "/";
    const scrollMap = getOverlayScrollMap();
    scrollMap[key] = window.scrollY || window.pageYOffset || 0;
    setOverlayScrollMap(scrollMap);
  });
};

const restoreRememberedScroll = () => {
  const key = window.location.pathname.replace(/\/$/, "") || "/";
  const scrollMap = getOverlayScrollMap();

  if (typeof scrollMap[key] !== "number") return;

  const targetY = scrollMap[key];
  delete scrollMap[key];
  setOverlayScrollMap(scrollMap);

  requestAnimationFrame(() => {
    window.scrollTo(0, targetY);
  });
};

const syncNavActiveState = () => {
  const links = document.querySelectorAll(".c-site-nav a, .c-nav-overlay a");
  const currentPath = window.location.pathname.replace(/\/$/, "");

  links.forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const linkPath = link.pathname.replace(/\/$/, "");
    const isActive = linkPath === currentPath;
    if (isActive) {
      link.setAttribute("aria-current", "page");
    } else {
      link.removeAttribute("aria-current");
    }
  });
};

const forceTextLinksNewTab = () => {
  const textLinks = document.querySelectorAll(".t-text a, .t-rich-text a");

  textLinks.forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const href = (link.getAttribute("href") || "").trim();
    if (!href || href.startsWith("#")) return;

    link.setAttribute("target", "_blank");

    const rel = (link.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
    const relSet = new Set(rel);
    relSet.add("noopener");
    relSet.add("noreferrer");
    link.setAttribute("rel", Array.from(relSet).join(" "));
  });
};

const initPageComponents = () => {
  initBlurUp();
  initFilters();
  destroyMarquee();
  initMarquee();
  resetFooterIllustration();
  initFooterIllustration();
  initFooterReveal();
  initHeroLogo();
  initHomeBackground();
  initServicesHighlight();
  initMembersHeroCount();
  initMembersOnlyGate();
  initCtaCardGlow();
  initOverlayClose();
  initInlineOverlay();
  initNewsletterCopy();
  forceTextLinksNewTab();
};

const initSwup = () => {
  const swup = new Swup({
    containers: ["#main"],
    animateHistoryBrowsing: true,
    animationSelector: "#main",
    linkSelector:
      'a[href^="' +
      window.location.origin +
      '"]:not([data-no-swup]):not([data-overlay]):not(.c-lang-switch__link), a[href^="/"]:not([data-no-swup]):not([data-overlay]):not(.c-lang-switch__link)',
    cache: true,
    skipPopStateHandling: false,
    plugins: [
      new SwupScrollPlugin({
        animateScroll: false,
      }),
      new SwupBodyClassPlugin({
        prefix: "site-page-",
      }),
    ],
  });

  window.swup = swup;

  if (swup.hooks) {
    swup.hooks.on("content:replace", () => {
      syncNavActiveState();
    });

    swup.hooks.on("page:view", () => {
      requestAnimationFrame(() => {
        document.body.classList.remove("is-modal-open");
        closeMobileNav();
        initPageComponents();
        restoreRememberedScroll();
      });
    });
  }
};

document.addEventListener("DOMContentLoaded", () => {
  syncNavActiveState();
  initMobileNav();
  initLogoShuffle();
  initPageComponents();
  rememberOverlayTriggerScroll();
  restoreRememberedScroll();
  initSwup();
});
